/**
 * Hook that processes WebSocket events and updates application stores.
 * Should be used once at the app level (e.g., in Dashboard or App).
 */
import { useEffect, useRef } from 'react';
import { useWebSocket } from './useWebSocket';

// Define the event types we handle
const WS_EVENT_TYPES = {
  PORTFOLIO_UPDATE: 'portfolio_update',
  ORDER_UPDATE: 'order_update',
  AGENT_STATUS: 'agent_status',
  AGENT_DECISION: 'agent_decision',
  RISK_STATUS: 'risk_status',
  PDT_STATUS: 'pdt_status',
  PLATFORM_STATUS: 'platform_status',
  NOTIFICATION: 'notification',
} as const;

/** Portfolio update event data from WebSocket. */
export interface PortfolioUpdateEvent {
  portfolio_value: number;
  cash: number;
  buying_power: number;
  total_gain_loss: number;
  total_gain_loss_percent: number;
  day_gain_loss: number;
  day_gain_loss_percent: number;
  positions_count: number;
}

/** Order update event data from WebSocket. */
export interface OrderUpdateEvent {
  order_id: string;
  symbol: string;
  side: string;
  order_type: string;
  status: string;
  quantity: number;
  filled_quantity: number;
  price?: number;
  filled_avg_price?: number;
}

/** Agent status event data from WebSocket. */
export interface AgentStatusEvent {
  state: string;
  message: string;
  last_activity?: string;
  trades_today: number;
  active_positions: number;
}

/** Agent decision event data from WebSocket. */
export interface AgentDecisionEvent {
  decision_id: string;
  decision_type: string;
  symbol?: string;
  reasoning: string;
  confidence: number;
  order_id?: string;
  awaiting_approval?: boolean;
  quantity?: number;
  price?: number;
  notional?: number;
  outcome?: string;
}

/** Risk status event data from WebSocket. */
export interface RiskStatusEvent {
  risk_level: string;
  daily_loss: number;
  daily_loss_limit: number;
  daily_loss_percent: number;
  max_position_size: number;
  current_exposure: number;
  warnings: string[];
}

/** PDT status event data from WebSocket. */
export interface PdtStatusEvent {
  day_trades_used: number;
  day_trades_remaining: number;
  day_trade_limit: number;
  is_pdt_restricted: boolean;
}

/** Platform status event data from WebSocket. */
export interface PlatformStatusEvent {
  platform_state: string;
  market_state: string;
  alpaca_connected: boolean;
  database_connected: boolean;
  message?: string;
}

/** Notification event data from WebSocket. */
export interface NotificationEvent {
  notification_id: string;
  level: string;
  title: string;
  message: string;
  action_url?: string;
  auto_dismiss: boolean;
}

interface WebSocketMessage {
  event: string;
  data?: unknown;
  timestamp?: string;
}

interface UseWebSocketEventsOptions {
  onPortfolioUpdate?: (data: PortfolioUpdateEvent) => void;
  onOrderUpdate?: (data: OrderUpdateEvent) => void;
  onAgentStatus?: (data: AgentStatusEvent) => void;
  onAgentDecision?: (data: AgentDecisionEvent) => void;
  onRiskStatus?: (data: RiskStatusEvent) => void;
  onPdtStatus?: (data: PdtStatusEvent) => void;
  onPlatformStatus?: (data: PlatformStatusEvent) => void;
  onNotification?: (data: NotificationEvent) => void;
}

export function useWebSocketEvents(options: UseWebSocketEventsOptions = {}) {
  const { lastMessage, isConnected } = useWebSocket();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!lastMessage) return;

    try {
      // lastMessage is already parsed by useWebSocket as { event, data }
      const message: WebSocketMessage = lastMessage;
      const { event, data } = message;

      switch (event) {
        case WS_EVENT_TYPES.PORTFOLIO_UPDATE:
          optionsRef.current.onPortfolioUpdate?.(data as PortfolioUpdateEvent);
          break;
        case WS_EVENT_TYPES.ORDER_UPDATE:
          optionsRef.current.onOrderUpdate?.(data as OrderUpdateEvent);
          break;
        case WS_EVENT_TYPES.AGENT_STATUS:
          optionsRef.current.onAgentStatus?.(data as AgentStatusEvent);
          break;
        case WS_EVENT_TYPES.AGENT_DECISION:
          optionsRef.current.onAgentDecision?.(data as AgentDecisionEvent);
          break;
        case WS_EVENT_TYPES.RISK_STATUS:
          optionsRef.current.onRiskStatus?.(data as RiskStatusEvent);
          break;
        case WS_EVENT_TYPES.PDT_STATUS:
          optionsRef.current.onPdtStatus?.(data as PdtStatusEvent);
          break;
        case WS_EVENT_TYPES.PLATFORM_STATUS:
          optionsRef.current.onPlatformStatus?.(data as PlatformStatusEvent);
          break;
        case WS_EVENT_TYPES.NOTIFICATION:
          optionsRef.current.onNotification?.(data as NotificationEvent);
          break;
      }
    } catch (error) {
      console.error('Error processing WebSocket message:', error);
    }
  }, [lastMessage]);

  return { isConnected };
}
